import { useCallback } from 'react';
import { graphql } from 'react-relay';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import type { ContentBodyType } from '../__types__/index.ts';
import useUpdateCachedContentDocumentContentFragment, {
	type useUpdateCachedContentDocumentContentFragment$data,
} from './__generated__/useUpdateCachedContentDocumentContentFragment.graphql.ts';

export const useUpdateCachedContentDocument = ({
	contentId,
	contentBody,
}: {
	contentId: string;
	contentBody: ContentBodyType;
}): ((newAdfDoc: JSONDocNode) => void) => {
	const writeFragmentInterop = useWriteFragmentInterop<
		Omit<useUpdateCachedContentDocumentContentFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: useUpdateCachedContentDocumentContentFragment,
		fragmentSpec: graphql`
			fragment useUpdateCachedContentDocumentContentFragment on Content {
				body {
					dynamic {
						representation
						value
						webresource {
							superbatch {
								tags {
									js
									css
								}
							}
							tags {
								js
								css
							}
						}
					}
				}
			}
		`,
	});

	return useCallback(
		(newAdfDoc: JSONDocNode) => {
			if (!contentBody) {
				return;
			}

			writeFragmentInterop({
				fragmentId: `Content:${contentId}`,
				data: {
					body: {
						dynamic: {
							...contentBody,
							value: JSON.stringify(newAdfDoc),
						},
					},
				},
			});
		},
		[contentBody, contentId, writeFragmentInterop],
	);
};
